import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../config';
import './AdminDrafts.css';

const DEFAULT_VISITOR_SUBJECT = 'Hey {{name}}, you were this close';
const DEFAULT_VISITOR_BODY = `Hey {{name}},

You stopped by dmyor and checked out the Create page, but never started your vision.

Most people who do that already have something in mind. Something they want but haven't written down yet.

That's exactly what dmyor is for.

→ https://dmyor.com/dashboard/create

The dmyor team`;

const serverConfig = { withCredentials: true };

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

const DEFAULT_SUBJECT = 'Hey {{name}}, your vision is still waiting';
const DEFAULT_BODY = `Hey {{name}},

You started building your {{duration}} vision on dmyor, "{{draftName}}", but never planted it.

The reality you wrote is still there. All it needs is for you to come back and finish it.

Every day you wait is a day you're not building toward it.

→ https://dmyor.com/dashboard/year-plans

The dmyor team`;

const STATUS_BADGE = {
  active:       { label: 'Active',       cls: 'ad-status-active' },
  converted:    { label: 'Converted',    cls: 'ad-status-converted' },
  unsubscribed: { label: 'Unsubscribed', cls: 'ad-status-unsub' },
};

const FILTERS = [
  { key: 'all',          label: 'All' },
  { key: 'active',       label: 'Active' },
  { key: 'converted',    label: 'Converted' },
  { key: 'unsubscribed', label: 'Unsubscribed' },
  { key: 'emailed',      label: 'Emailed' },
  { key: 'not_emailed',  label: 'Not emailed' },
];

const AdminDrafts = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('active');

  // Visitors section state
  const [visitorsData, setVisitorsData] = useState(null);
  const [visitorsLoading, setVisitorsLoading] = useState(false);
  const [visitorsExpanded, setVisitorsExpanded] = useState(false);
  const [selectedVisitors, setSelectedVisitors] = useState(new Set());
  const [visitorSubject, setVisitorSubject] = useState(DEFAULT_VISITOR_SUBJECT);
  const [visitorBody, setVisitorBody] = useState(DEFAULT_VISITOR_BODY);
  const [visitorSending, setVisitorSending] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data: res } = await axios.get(`${config.apiUrl}/api/v1/admin/drafts`, serverConfig);
      setData(res);
    } catch {
      toast.error('Failed to load draft users');
    } finally {
      setLoading(false);
    }
  };

  const fetchVisitors = async () => {
    try {
      setVisitorsLoading(true);
      const { data: res } = await axios.get(`${config.apiUrl}/api/v1/admin/create-page-visitors`, serverConfig);
      setVisitorsData(res);
    } catch {
      toast.error('Failed to load create-page visitors');
    } finally {
      setVisitorsLoading(false);
    }
  };

  const toggleVisitorSelect = (userId) => {
    setSelectedVisitors((prev) => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const toggleSelectAllVisitors = () => {
    const ids = (visitorsData?.visitors || []).map((v) => v._id).filter(Boolean);
    const allSel = ids.every((id) => selectedVisitors.has(id));
    setSelectedVisitors((prev) => {
      const next = new Set(prev);
      allSel ? ids.forEach((id) => next.delete(id)) : ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleSendVisitors = async () => {
    if (!selectedVisitors.size) return toast.error('Select at least one user');
    if (!visitorSubject.trim() || !visitorBody.trim()) return toast.error('Subject and body are required');
    try {
      setVisitorSending(true);
      const { data: res } = await axios.post(
        `${config.apiUrl}/api/v1/admin/create-page-visitors/email`,
        { userIds: [...selectedVisitors], subject: visitorSubject, body: visitorBody },
        serverConfig
      );
      toast.success(`Sent to ${res.sent} user${res.sent !== 1 ? 's' : ''}${res.failed ? `, ${res.failed} failed` : ''}`);
      setSelectedVisitors(new Set());
      fetchVisitors();
    } catch {
      toast.error('Failed to send emails');
    } finally {
      setVisitorSending(false);
    }
  };

  const toggleSelect = (userId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visible = filteredPings.map((p) => p.user?._id).filter(Boolean);
    const allSel = visible.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      allSel ? visible.forEach((id) => next.delete(id)) : visible.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleSend = async () => {
    if (!selected.size) return toast.error('Select at least one user');
    if (!subject.trim() || !body.trim()) return toast.error('Subject and body are required');
    try {
      setSending(true);
      const { data: res } = await axios.post(
        `${config.apiUrl}/api/v1/admin/drafts/email`,
        { userIds: [...selected], subject, body },
        serverConfig
      );
      toast.success(`Sent to ${res.sent} user${res.sent !== 1 ? 's' : ''}${res.failed ? `, ${res.failed} failed` : ''}`);
      setSelected(new Set());
      fetchData();
    } catch {
      toast.error('Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  const filteredPings = (data?.pings || []).filter((p) => {
    if (filter === 'active')       return p.status === 'active';
    if (filter === 'converted')    return p.status === 'converted';
    if (filter === 'unsubscribed') return p.status === 'unsubscribed';
    if (filter === 'emailed')      return p.emailSent;
    if (filter === 'not_emailed')  return p.status === 'active' && !p.emailSent;
    return true;
  });

  const visibleIds = filteredPings.map((p) => p.user?._id).filter(Boolean);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  if (loading) return <div className="ad-page"><p className="ad-loading">Loading...</p></div>;

  return (
    <div className="ad-page">
      <div className="ad-header">
        <div className="ad-eyebrow">Admin · Draft Users</div>
        <h1 className="ad-title">Users Who Saved Drafts</h1>
        <p className="ad-sub">People who started a vision and haven't planted it, or have since converted.</p>
      </div>

      {/* Stats */}
      <div className="ad-stats">
        <div className="ad-stat">
          <div className="ad-stat-value">{data?.totalDraftUsers ?? 0}</div>
          <div className="ad-stat-label">Total</div>
        </div>
        <div className="ad-stat">
          <div className="ad-stat-value">{data?.activeCount ?? 0}</div>
          <div className="ad-stat-label">Active</div>
        </div>
        <div className="ad-stat">
          <div className="ad-stat-value ad-stat-green">{data?.convertedCount ?? 0}</div>
          <div className="ad-stat-label">Converted</div>
        </div>
        <div className="ad-stat">
          <div className="ad-stat-value">{data?.unsubscribedCount ?? 0}</div>
          <div className="ad-stat-label">Unsubscribed</div>
        </div>
        <div className="ad-stat">
          <div className="ad-stat-value ad-stat-orange">{data?.emailedCount ?? 0}</div>
          <div className="ad-stat-label">Emailed</div>
        </div>
        <div className="ad-stat">
          <div className="ad-stat-value">{selected.size}</div>
          <div className="ad-stat-label">Selected</div>
        </div>
      </div>

      <div className="ad-body">
        {/* Left: user list */}
        <div className="ad-left">
          <div className="ad-table-toolbar">
            <div className="ad-filter-tabs">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  className={`ad-filter-tab ${filter === f.key ? 'active' : ''}`}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <button className="ad-select-all" onClick={toggleSelectAll}>
              {allVisibleSelected ? 'Deselect all' : 'Select all'}
            </button>
          </div>

          {filteredPings.length === 0 ? (
            <p className="ad-empty">No users match this filter.</p>
          ) : (
            <div className="ad-table">
              {filteredPings.map((ping) => {
                const userId = ping.user?._id;
                const isSelected = selected.has(userId);
                const badge = STATUS_BADGE[ping.status] || STATUS_BADGE.active;
                const canSelect = ping.status === 'active';

                return (
                  <div
                    key={ping._id}
                    className={`ad-row ${isSelected ? 'selected' : ''} ${!canSelect ? 'ad-row-muted' : ''}`}
                    onClick={() => canSelect && toggleSelect(userId)}
                  >
                    <div className="ad-row-check">
                      {canSelect ? (
                        <div className={`ad-checkbox ${isSelected ? 'checked' : ''}`}>
                          {isSelected && <span>✓</span>}
                        </div>
                      ) : (
                        <div className="ad-checkbox ad-checkbox-disabled" />
                      )}
                    </div>
                    <div className="ad-row-body">
                      <div className="ad-row-top">
                        <span className="ad-row-name">{ping.user?.name || '—'}</span>
                        <span className={`ad-row-status ${badge.cls}`}>{badge.label}</span>
                        {ping.emailSent && ping.status === 'active' && (
                          <span className="ad-row-badge-emailed">
                            Emailed {formatDate(ping.emailSentAt)}
                          </span>
                        )}
                        {ping.status === 'converted' && ping.convertedAt && (
                          <span className="ad-row-badge-converted">
                            {formatDate(ping.convertedAt)}
                          </span>
                        )}
                      </div>
                      <div className="ad-row-email">{ping.user?.email || '—'}</div>
                      <div className="ad-row-meta">
                        <span>{ping.draftName}</span>
                        {ping.durationInYears && <span>· {ping.durationInYears}y vision</span>}
                        {ping.filledCount > 0 && <span>· {ping.filledCount} dimension{ping.filledCount !== 1 ? 's' : ''}</span>}
                        <span className="ad-row-date">Saved {formatDate(ping.savedAt)}</span>
                      </div>
                      {ping.categories?.length > 0 && (
                        <div className="ad-row-cats">
                          {ping.categories.slice(0, 4).map((c) => (
                            <span key={c} className="ad-cat-pill">{c}</span>
                          ))}
                          {ping.categories.length > 4 && (
                            <span className="ad-cat-pill ad-cat-more">+{ping.categories.length - 4}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: compose */}
        <div className="ad-right">
          <div className="ad-compose">
            <div className="ad-compose-header">
              <span className="ad-compose-title">Compose Email</span>
              <span className="ad-compose-to">
                {selected.size > 0
                  ? `To: ${selected.size} user${selected.size !== 1 ? 's' : ''}`
                  : 'Select active users from the list'}
              </span>
            </div>

            <div className="ad-compose-hint">
              Use <code>{'{{name}}'}</code>, <code>{'{{draftName}}'}</code>, <code>{'{{duration}}'}</code> for personalisation.
              Unsubscribe link is appended automatically.
            </div>

            <div className="ad-field">
              <label className="ad-label">Subject</label>
              <input className="ad-input" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>

            <div className="ad-field">
              <label className="ad-label">Body</label>
              <textarea className="ad-textarea" value={body} onChange={(e) => setBody(e.target.value)} rows={14} />
            </div>

            <button
              className="ad-send-btn"
              onClick={handleSend}
              disabled={sending || selected.size === 0}
            >
              {sending ? 'Sending...' : `Send to ${selected.size} user${selected.size !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
      {/* ── Visited but didn't act ── */}
      <div className="ad-visitors-section">
        <button
          className="ad-visitors-toggle"
          onClick={() => {
            if (!visitorsExpanded && !visitorsData) fetchVisitors();
            setVisitorsExpanded((v) => !v);
          }}
        >
          <span>Visited create page · didn't start</span>
          <span className="ad-visitors-toggle-arrow">{visitorsExpanded ? '▲' : '▼'}</span>
        </button>

        {visitorsExpanded && (
          <div className="ad-visitors-body">
            {visitorsLoading ? (
              <p className="ad-loading">Loading…</p>
            ) : (
              <>
                <div className="ad-visitors-meta">
                  <span>{visitorsData?.total ?? 0} users visited the create page without starting or drafting anything</span>
                  <button className="ad-select-all" onClick={toggleSelectAllVisitors}>
                    {(visitorsData?.visitors || []).every((v) => selectedVisitors.has(v._id))
                      ? 'Deselect all'
                      : 'Select all'}
                  </button>
                </div>

                <div className="ad-visitors-layout">
                  <div className="ad-left">
                    {(visitorsData?.visitors || []).length === 0 ? (
                      <p className="ad-empty">No passive visitors yet.</p>
                    ) : (
                      <div className="ad-table">
                        {(visitorsData.visitors).map((visitor) => {
                          const isSelected = selectedVisitors.has(visitor._id);
                          return (
                            <div
                              key={visitor._id}
                              className={`ad-row ${isSelected ? 'selected' : ''}`}
                              onClick={() => toggleVisitorSelect(visitor._id)}
                            >
                              <div className="ad-row-check">
                                <div className={`ad-checkbox ${isSelected ? 'checked' : ''}`}>
                                  {isSelected && <span>✓</span>}
                                </div>
                              </div>
                              <div className="ad-row-body">
                                <div className="ad-row-top">
                                  <span className="ad-row-name">{visitor.name || '—'}</span>
                                  {visitor.marketingUnsubscribed && (
                                    <span className="ad-row-status ad-status-unsub">Unsubscribed</span>
                                  )}
                                </div>
                                <div className="ad-row-email">{visitor.email || '—'}</div>
                                <div className="ad-row-meta">
                                  {visitor.country && <span>{visitor.country}</span>}
                                  <span className="ad-row-date">Visited {formatDate(visitor.visitedAt)}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="ad-right">
                    <div className="ad-compose">
                      <div className="ad-compose-header">
                        <span className="ad-compose-title">Compose Email</span>
                        <span className="ad-compose-to">
                          {selectedVisitors.size > 0
                            ? `To: ${selectedVisitors.size} user${selectedVisitors.size !== 1 ? 's' : ''}`
                            : 'Select users from the list'}
                        </span>
                      </div>
                      <div className="ad-compose-hint">
                        Use <code>{'{{name}}'}</code> for personalisation. Unsubscribe link appended automatically.
                      </div>
                      <div className="ad-field">
                        <label className="ad-label">Subject</label>
                        <input className="ad-input" value={visitorSubject} onChange={(e) => setVisitorSubject(e.target.value)} />
                      </div>
                      <div className="ad-field">
                        <label className="ad-label">Body</label>
                        <textarea className="ad-textarea" value={visitorBody} onChange={(e) => setVisitorBody(e.target.value)} rows={14} />
                      </div>
                      <button
                        className="ad-send-btn"
                        onClick={handleSendVisitors}
                        disabled={visitorSending || selectedVisitors.size === 0}
                      >
                        {visitorSending ? 'Sending…' : `Send to ${selectedVisitors.size} user${selectedVisitors.size !== 1 ? 's' : ''}`}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDrafts;
